import { lazy, Suspense } from "react";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/Seo";
import SolarrechnerButton from "@/components/SolarrechnerButton";
import { Link } from "react-router-dom";

// Below-the-fold: lazy-load to shrink initial JS on mobile.
const GoogleReviews = lazy(() => import("@/components/GoogleReviews"));
const HelmutTestimonial = lazy(() => import("@/components/HelmutTestimonial"));
const BeispieleSlideshow = lazy(() => import("@/components/BeispieleSlideshow"));
const CalendlyWidget = lazy(() => import("@/components/CalendlyWidget"));
// RueckrufFormular ist interaktiv (Form-State, kein SEO-Inhalt) → lazy für TBT-Schutz.
// GarantieBadges enthält wichtige Text-Inhalte (30 Jahre Modul-Garantie etc.)
// → eager, damit SSR-Output diese Conversion-Trust-Signale enthält.
const RueckrufFormular = lazy(() => import("@/components/RueckrufFormular"));
import LazyOnVisible from "@/components/LazyOnVisible";
import GarantieBadges from "@/components/GarantieBadges";
import { Sun, Battery, Plug, Cpu, ShieldCheck, Wrench, Sparkles, ArrowRight, TrendingDown, Receipt, Clock, BadgeCheck, Handshake, Truck, Headphones, MapPin, Zap, MessageCircle } from "lucide-react";
import { HERSTELLER } from "@/data/hersteller";
import { REFERENZEN } from "@/data/referenzen";
import { LATEST_PROJECT } from "@/data/latest-project";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import process01 from "@/assets/process-01-anfrage.jpg";
import process02 from "@/assets/process-02-beratung.jpg";
import process03 from "@/assets/process-03-installation.jpg";
import process04 from "@/assets/process-04-service.jpg";
import heroDesktop from "@/assets/wm-hero-desktop.webp";
import heroDesktopAvif from "@/assets/wm-hero-desktop.avif";
import heroMobile from "@/assets/wm-hero-mobile.webp";
import heroMobileAvif from "@/assets/wm-hero-mobile.avif";
import { buildSrcSet } from "@/lib/responsive-image";
import { REVIEW_SUMMARY } from "@/data/reviews";

const INDEX_FAQS = [
  { q: "Lohnt sich eine PV-Anlage 2026 noch?", a: "Eindeutig ja. Modulpreise auf Rekordtief, 0 % Mehrwertsteuer auf Anlagen bis 30 kWp und Strompreise von 35–40 ct/kWh in Baden-Württemberg machen Eigenverbrauch attraktiver denn je. Eine typische 10-kWp-Anlage mit Speicher amortisiert sich in der Rhein-Neckar-Region in 10–13 Jahren – danach 15+ Jahre praktisch kostenloser Strom. Stand: Mai 2026." },
  { q: "Was kostet eine PV-Anlage insgesamt, inklusive Montage und allem?", a: "Eine schlüsselfertige 10-kWp-Anlage liegt brutto = netto (dank 0 % MwSt.) bei rund 16.000–22.000 €, inklusive Modulen, Montage, Wechselrichter, Anmeldung und Inbetriebnahme. Mit 8–10-kWh-Speicher rechnest du 22.000–30.000 €. Wir erstellen Festpreis-Angebote ohne Vorkasse und ohne versteckte Posten." },
  { q: "Wie lange dauert die Installation einer PV-Anlage wirklich?", a: "Reine Montage auf dem Dach: 1–2 Werktage. Elektroanschluss und Inbetriebnahme: 1–2 weitere Tage. Vom Auftrag bis zur Inbetriebnahme rechnen wir aktuell mit 6–12 Wochen – die meiste Zeit geht für Materialbeschaffung und Netzbetreiber-Anmeldung drauf, nicht für die Montage selbst." },
  { q: "Habe ich bei einem Stromausfall trotzdem Strom?", a: "Standard-PV-Anlagen schalten bei Netzausfall ab. Mit Notstrom-Option (bei Sigenergy „Notstrom-Ready\" für +1.000 €) läuft die Anlage im Inselbetrieb weiter und versorgt deinen Haushalt. Für unterbrechungsfreie Versorgung sensibler Geräte gibt es Speicher mit Umschaltzeit unter 20 ms." },
  { q: "Wie wartungsintensiv ist eine PV-Anlage?", a: "Sehr wartungsarm. Module reinigen sich durch Regen selbst. Der Wechselrichter ist die einzige aktive Komponente und hält typischerweise 10–15 Jahre. Monitoring ist bei uns inklusive – bei Fehlern wirst du automatisch kontaktiert." }
];

const indexJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://wm-erneuerbareenergien.com/#organization",
    name: "WM Erneuerbare Energien GmbH",
    url: "https://wm-erneuerbareenergien.com/",
    telephone: "+49 1515 0559085",
    image: "https://wm-erneuerbareenergien.com/images/social-preview.webp",
    priceRange: "€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Mühlstraße 8",
      postalCode: "69234",
      addressLocality: "Dielheim",
      addressRegion: "Baden-Württemberg",
      addressCountry: "DE",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: REVIEW_SUMMARY.rating,
      bestRating: 5,
      worstRating: 1,
      reviewCount: REVIEW_SUMMARY.count,
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: INDEX_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

const Index = () => {
  return (
    <Layout>
      <Seo
        title="Solarteur Rhein-Neckar – Photovoltaik in deiner Nähe | WMEE"
        description="Solarteur aus der Rhein-Neckar-Region. Über 800 Projekte seit 2012: PV-Anlagen, Speicher, Wallboxen und Energiemanagement – herstellerunabhängig."
        path="/"
        jsonLd={indexJsonLd}
      />
      {/* HERO */}
      <section className="relative w-full min-h-[85vh] lg:min-h-[92vh] flex items-center overflow-hidden pt-24 pb-16 md:py-0">
        <picture>
          <source type="image/avif" media="(max-width: 767px)" srcSet={heroMobileAvif} />
          <source type="image/avif" srcSet={heroDesktopAvif} />
          <source type="image/webp" media="(max-width: 767px)" srcSet={heroMobile} />
          <img
            src={heroDesktop}
            alt="Modernes Haus mit Photovoltaik-Anlage"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </picture>
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-overlay)" }} />
        <div
          className="absolute inset-0 pointer-events-none hidden lg:block"
          style={{
            background:
              "linear-gradient(to right, transparent 0%, transparent 30%, hsl(var(--foreground) / 0.7) 60%, hsl(var(--foreground)) 80%)",
          }}
        />
        <div className="absolute inset-0 pointer-events-none lg:hidden bg-foreground/40" />
        <div className="container-wm relative z-10">
          <div className="max-w-3xl animate-fade-up lg:-ml-6 xl:-ml-10">
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-4 drop-shadow-[0_3px_12px_rgba(0,0,0,0.7)]">
              <span className="sr-only">Photovoltaik in der Rhein-Neckar-Region – </span>
              Dein Zuhause.
              <br />
              Deine Energie.
              <br />
              <span className="hero-accent">Weniger Stromkosten.</span>
            </h1>
            <p className="text-base sm:text-lg md:text-2xl text-white/95 mb-8 md:mb-10 font-normal leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
              Mit Photovoltaik, Speicher und Wallbox machst du dich unabhängiger von steigenden Energiepreisen –
              <span className="hidden md:inline"><br /></span>{" "}
              geplant, installiert und betreut von WMEE für Wiesloch, Walldorf, Heidelberg und die gesamte Rhein-Neckar-Region.
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              <SolarrechnerButton label="Berechne den Preis deiner PV" className="w-full sm:w-auto justify-center" />
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white/15 hover:bg-white/25 text-white px-7 py-3 min-h-[48px] font-semibold uppercase tracking-wide text-sm backdrop-blur transition-colors border border-white/30 w-full sm:w-auto"
              >
                Persönliches Erstgespräch buchen
              </Link>
            </div>
          </div>
        </div>

        {/* Vertikaler Referenzen-Slider rechts (nur Desktop) */}
        <div className="hidden lg:block absolute top-1/2 -translate-y-1/2 right-16 xl:right-24 h-[70%] w-[340px] xl:w-[400px] z-20 pointer-events-none">
          <p className="text-white/90 text-sm font-semibold uppercase tracking-widest mb-3 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] text-right pr-2">
            Bereits über 800 realisierte Projekte:
          </p>
          <div className="relative h-[calc(100%-2rem)] overflow-hidden">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 z-10 bg-gradient-to-b from-foreground to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 z-10 bg-gradient-to-t from-foreground to-transparent" />
            <div className="flex flex-col gap-5 animate-marquee-vertical">
              {[
                { image: "/images/hero-slider-1-walldorf.webp", ort: "Walldorf" },
                { image: "/images/hero-slider-2-altlussheim.webp", ort: "Altlußheim" },
                { image: "/images/hero-slider-3-moenchzell.webp", ort: "Mönchzell" },
                { image: "/images/hero-slider-4.webp", ort: "Referenzanlage" },
                { image: "/images/hero-slider-1-walldorf.webp", ort: "Walldorf" },
                { image: "/images/hero-slider-2-altlussheim.webp", ort: "Altlußheim" },
                { image: "/images/hero-slider-3-moenchzell.webp", ort: "Mönchzell" },
                { image: "/images/hero-slider-4.webp", ort: "Referenzanlage" },
              ].map((r, i) => (
                <div
                  key={`${r.image}-${i}`}
                  className="shrink-0 rounded-2xl overflow-hidden border border-white/10 shadow-2xl aspect-[4/3]"
                >
                  <img src={r.image} srcSet={buildSrcSet(r.image, 1280)} sizes="(min-width: 768px) 400px, 80vw" alt={`PV-Anlage ${r.ort}`} loading="lazy" decoding="async" width={400} height={300} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DEINE VORTEILE – Asymmetrischer Split mit großer Zahl */}
      <section className="section bg-card/30 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="container-wm relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-4">Seit 2012 in der Region</p>
              <div className="flex items-baseline gap-3 mb-6 flex-wrap">
                <span className="text-5xl sm:text-7xl md:text-[10rem] leading-none font-bold text-primary tracking-tight">14</span>
                <span className="text-xl sm:text-3xl md:text-4xl text-foreground font-semibold">+ Jahre</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-5xl mb-5 leading-tight">
                Deine Vorteile mit<br />
                <span className="text-primary">WM Erneuerbaren Energien</span>
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                Vom ersten Beratungsgespräch bis zum Service Jahre nach der Inbetriebnahme – alles aus einer Hand.
                Auch bestehende Anlagen bringen wir mit unserer Optimierung auf ein neues Niveau.
              </p>
              <SolarrechnerButton label="Kostenlos Ersparnis prüfen" className="text-base px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto" />
            </div>

            <div className="lg:col-span-7 space-y-4">
              {[
                { icon: ShieldCheck, title: "Langjährige Erfahrung", text: "Über ein Jahrzehnt Know-how. Wir wissen, worauf es wirklich ankommt.", offset: "lg:ml-0" },
                { icon: Handshake, title: "Alles aus einer Hand", text: "Beratung, Planung, Montage & Service aus unserem Team.", offset: "lg:ml-12" },
                { icon: Sparkles, title: "Premium-Qualität", text: "Nur geprüfte Komponenten mit langen Garantien.", offset: "lg:ml-6" },
                { icon: Cpu, title: "Individuelle Planung", text: "Keine Lösung von der Stange – deine Anlage wird individuell geplant.", offset: "lg:ml-16" },
                { icon: Wrench, title: "Optimierung von Bestandsanlagen", text: "Bestehende PV-Anlagen prüfen, modernisieren und mit Speicher oder Wallbox erweitern.", offset: "lg:ml-8" },
              ].map((c) => (
                <div
                  key={c.title}
                  className={`${c.offset} flex items-center gap-4 sm:gap-5 bg-background border border-border rounded-2xl p-4 sm:p-5 hover:border-primary/60 lg:hover:translate-x-2 transition-all duration-300 shadow-sm`}
                >
                  <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                    <c.icon size={24} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold mb-1 text-base sm:text-lg">{c.title}</h3>
                    <p className="text-sm text-muted-foreground leading-snug">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* UNSER NEUESTES PROJEKT – frisch + lokal, manuell pflegbar in src/data/latest-project.ts */}
      <section className="section bg-card/40 hidden">
        <div className="container-wm max-w-5xl">
          <p className="eyebrow mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            Frisch übergeben
          </p>
          <h2 className="text-3xl md:text-4xl mb-6 leading-tight">
            Unser neuestes Projekt –{" "}
            <span className="text-primary">{LATEST_PROJECT.kwp} kWp in {LATEST_PROJECT.city}</span>
          </h2>
          <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-center bg-background border border-border rounded-2xl p-5 md:p-7 shadow-sm">
            <div className="md:col-span-5">
              <img
                src="/images/AdobeStock_688195352-800w.webp"
                srcSet="/images/AdobeStock_688195352-480w.webp 480w, /images/AdobeStock_688195352-800w.webp 800w"
                sizes="(min-width: 768px) 50vw, 100vw"
                alt={`Neueste PV-Anlage ${LATEST_PROJECT.kwp} kWp in ${LATEST_PROJECT.city}, übergeben am ${LATEST_PROJECT.dateLabel}`}
                width={800}
                height={600}
                loading="lazy"
                decoding="async"
                className="w-full h-auto rounded-xl border border-border object-cover aspect-[4/3] bg-card"
              />
            </div>
            <div className="md:col-span-7">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold bg-primary/15 text-primary rounded-full px-3 py-1">
                  <Sun size={14} /> {LATEST_PROJECT.kwp} kWp
                </span>
                {LATEST_PROJECT.storageKwh ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold bg-primary/15 text-primary rounded-full px-3 py-1">
                    <Battery size={14} /> {LATEST_PROJECT.storageKwh} kWh Speicher
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold bg-card border border-border rounded-full px-3 py-1">
                  Übergabe: {LATEST_PROJECT.dateLabel}
                </span>
              </div>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-5">
                {LATEST_PROJECT.description}
              </p>
              {LATEST_PROJECT.citySlug && (
                <Link
                  to={`/standorte/${LATEST_PROJECT.citySlug}`}
                  className="inline-flex items-center gap-2 text-primary font-semibold hover:underline"
                >
                  Photovoltaik in {LATEST_PROJECT.city} <ArrowRight size={16} />
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <Suspense fallback={null}>
        <HelmutTestimonial />
        <GoogleReviews />
      </Suspense>

      {/* UNSERE GARANTIEVERSPRECHEN – eager wegen SEO-Trust-Signalen im SSR-Output */}
      <GarantieBadges
        heading={{
          eyebrow: "Unsere Garantieversprechen",
          title: "Sicherheit für die nächsten Jahrzehnte",
          subtitle: "Premium-Hersteller mit langen Garantielaufzeiten – und WMEE als Service-Ansprechpartner über die gesamte Lebensdauer deiner Anlage.",
        }}
        bgClassName="bg-card/30"
      />

      {/* LOHNT SICH PV IN 2026 + Beispiele Slideshow auf gemeinsamem dunklen Hintergrund */}
      <div className="relative overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-primary/20 blur-3xl pointer-events-none" />

        <section className="relative">
          <div className="grid lg:grid-cols-12 lg:items-stretch">
            <div className="lg:col-span-4 flex flex-col justify-center items-center text-center py-12 md:py-20 px-6 md:px-10 lg:pl-6 lg:pr-8 bg-primary text-primary-foreground">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary-foreground/40 bg-primary-foreground/10 text-primary-foreground text-sm font-semibold uppercase tracking-widest mb-6">
                <span className="w-2 h-2 rounded-full bg-primary-foreground animate-pulse" /> Aktuelle Lage 2026
              </div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.05] mb-6 md:mb-8">
                Lohnt sich PV<br />
                in 2026?
              </h2>
              <p className="text-xl sm:text-2xl md:text-3xl leading-snug mb-4">
                Kurz gesagt: <span className="font-bold">Ja – mehr denn je.</span>
              </p>
              <p className="text-base sm:text-lg md:text-xl text-primary-foreground/80 leading-relaxed">
                Modulpreise auf Rekordtief, Strompreise hoch, MwSt. weiter bei 0 %. Wer 2026 startet, sichert sich planbare Energiekosten für die nächsten 25+ Jahre.
              </p>
              <p className="text-xs text-primary-foreground/70 italic mt-4">
                Stand: Mai 2026
              </p>
            </div>
            <div className="lg:col-span-8 self-stretch">
              <Suspense fallback={<div className="min-h-[400px]" />}><BeispieleSlideshow embedded /></Suspense>
            </div>
          </div>
        </section>
      </div>

      {/* SO LÄUFT'S AB – Zigzag Prozess Timeline auf dunklem Hintergrund */}
      <section className="relative overflow-hidden bg-background text-foreground py-12 md:py-20 lg:py-28">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-primary/15 blur-3xl pointer-events-none" />

        <div className="container-wm relative">
          <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16 lg:mb-20">
            <div className="inline-flex items-center gap-2 text-primary text-xs sm:text-sm font-semibold uppercase tracking-widest mb-4">
              <MapPin size={16} /> So einfach geht's
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-5 leading-tight font-bold">
              Vom ersten Klick zur <span className="text-primary italic">eigenen Stromversorgung</span>
            </h2>
            <p className="text-base sm:text-lg text-foreground/80 leading-relaxed">
              Vier klare Etappen – und dauerhafte Unterstützung lange nach dem Aufbau. So sieht der Weg zu deinem eigenen Sonnenkraftwerk bei uns aus.
            </p>
          </div>

          <div className="max-w-6xl mx-auto relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-foreground/20 to-transparent -translate-x-px" />

            {[
              {
                num: "01",
                img: process01,
                title: "Kurz dein Projekt skizzieren",
                paragraphs: [
                  "Im Solarrechner trägst du in wenigen Minuten die wichtigsten Eckdaten zu deinem Zuhause ein – fertig.",
                  "So gehen wir vorbereitet in unser erstes Telefonat und sparen dir später unnötige Rückfragen.",
                ],
              },
              {
                num: "02",
                img: process02,
                title: "Vor-Ort-Termin & faires Festangebot",
                paragraphs: [
                  "Im Anschluss melden wir uns bei dir, klären offene Punkte und stimmen einen Besichtigungstermin ab.",
                  "Erst wenn wir Zählerschrank, Dachfläche und Leitungsführung selbst gesehen haben, schnüren wir dir ein nachvollziehbares Angebot – komplett unverbindlich.",
                ],
              },
              {
                num: "03",
                img: process03,
                title: "Saubere Montage – erst zahlen, wenn alles läuft",
                paragraphs: [
                  "Sagst du Ja, kümmern wir uns um die Bestellung der Hardware und unser Team baut deine Anlage handwerklich sauber auf.",
                  "Typisch WMEE: keine Vorkasse. Die Rechnung kommt erst, wenn alles in Betrieb ist und du wirklich zufrieden bist.",
                  "Den Papierkram mit dem Netzbetreiber und alle technischen Abstimmungen erledigen wir selbstverständlich für dich.",
                ],
              },
              {
                num: "04",
                img: process04,
                title: "Wir bleiben nach der Übergabe an deiner Seite",
                paragraphs: [
                  "Mit der Inbetriebnahme endet unsere Arbeit nicht – wir bleiben dein direkter Ansprechpartner.",
                  "Über das Monitoring behalten wir die Erträge im Blick, reagieren bei Auffälligkeiten sofort und sichern dich mit umfassenden Garantien ab. So bleibt das gute Gefühl auch noch viele Jahre später.",
                ],
              },
            ].map((step, i) => {
              const isLeft = i % 2 === 0;
              return (
                <div key={step.num} className="relative mb-12 md:mb-24 last:mb-0">
                  <div className="hidden md:flex absolute left-1/2 top-8 -translate-x-1/2 w-14 h-10 items-center justify-center bg-foreground text-background text-base font-semibold tabular-nums z-10 rounded-sm">
                    {step.num}
                  </div>

                  <div className="md:grid md:grid-cols-2 md:gap-16 items-center">
                    <div className={`${isLeft ? "md:order-1 md:pr-16 md:text-right" : "md:order-2 md:pl-16"} mb-6 md:mb-0`}>
                      <div className="md:hidden inline-flex items-center justify-center w-12 h-9 bg-foreground text-background text-sm font-semibold tabular-nums mb-3 rounded-sm">
                        {step.num}
                      </div>
                      <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold mb-4 md:mb-5 leading-tight">{step.title}</h3>
                      <div className="space-y-3 md:space-y-4">
                        {step.paragraphs.map((p, idx) => (
                          <p key={idx} className="text-sm sm:text-base text-foreground/80 leading-relaxed">{p}</p>
                        ))}
                      </div>
                    </div>

                    <div className={`${isLeft ? "md:order-2" : "md:order-1"}`}>
                      <div className="relative overflow-hidden rounded-lg shadow-2xl group">
                        <img
                          src={step.img}
                          alt={step.title}
                          loading="lazy"
                          width={1024}
                          height={768}
                          className="w-full h-auto aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10 md:mt-16">
            <SolarrechnerButton label="Berechne den Preis deiner neuen PV" className="text-base px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto" />
            <div className="mt-6 flex items-center justify-center gap-3 text-foreground/70 text-sm italic">
              <span className="flex -space-x-2">
                <span className="w-7 h-7 rounded-full bg-primary/40 border-2 border-background" />
                <span className="w-7 h-7 rounded-full bg-primary/60 border-2 border-background" />
                <span className="w-7 h-7 rounded-full bg-primary/80 border-2 border-background" />
              </span>
              Über 800 Anlagen wurden realisiert.
            </div>
          </div>
        </div>
      </section>





      {/* HERSTELLER LOGOS – Marquee */}
      <section className="section">
        <div className="container-wm">
          <div className="text-center mb-8 md:mb-10 max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl leading-tight">
              Qualität ist für uns nicht verhandelbar – deshalb setzen wir unter anderem auf diese <span className="text-primary">Premium-Hersteller</span>.
            </h2>
          </div>
        </div>
        <div className="relative overflow-hidden group">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent z-10" />
          <div className="flex w-max md:animate-marquee-rtl group-hover:[animation-play-state:paused]">
            {[...HERSTELLER, ...HERSTELLER].map((h, i) => (
              <a
                key={`${h.name}-${i}`}
                href={h.website}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${h.name} (${h.category}) Website öffnen`}
                className="shrink-0 mx-6 md:mx-10 flex flex-col items-center justify-center h-28 md:h-32 w-32 md:w-44"
              >
                <img
                  src={h.logo}
                  alt={`${h.name} – ${h.category}`}
                  loading="lazy"
                  decoding="async"
                  width={176}
                  height={64}
                  className="max-h-14 md:max-h-16 max-w-full object-contain opacity-80 hover:opacity-100 transition-opacity"
                />
                <p className="mt-2 text-[0.7rem] md:text-xs uppercase tracking-[0.15em] font-semibold text-primary text-center leading-tight">
                  {h.category}
                </p>
                {h.tagline && (
                  <p className="text-[0.65rem] md:text-[0.7rem] text-muted-foreground text-center leading-tight mt-0.5">
                    {h.tagline}
                  </p>
                )}
              </a>
            ))}
          </div>
        </div>
        <div className="container-wm text-center mt-10">
          <Link to="/empfehlungen" className="text-primary font-semibold hover:underline">
            Alle Hersteller ansehen →
          </Link>
        </div>
      </section>

      {/* FAQ – meistgeklickte Fragen */}
      <section className="pt-4 md:pt-6 pb-12 md:pb-20 bg-card/30">
        <div className="container-wm max-w-4xl">
          <div className="text-center mb-8 md:mb-10">
            <p className="eyebrow mb-3 md:mb-4 text-xs sm:text-sm">FAQ</p>
            <h2 className="text-2xl sm:text-3xl md:text-5xl leading-tight">
              Die <span className="text-primary">meistgeklickten Fragen</span> aus unseren FAQs
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              { q: "Lohnt sich eine PV-Anlage 2026 noch?", a: "Eindeutig ja. Modulpreise auf Rekordtief, 0 % Mehrwertsteuer auf Anlagen bis 30 kWp und Strompreise von 35–40 ct/kWh in Baden-Württemberg machen Eigenverbrauch attraktiver denn je. Eine typische 10-kWp-Anlage mit Speicher amortisiert sich in der Rhein-Neckar-Region in 10–13 Jahren – danach 15+ Jahre praktisch kostenloser Strom. Stand: Mai 2026." },
              { q: "Ist die Anfrage kostenlos und unverbindlich?", a: "Ja. Deine Anfrage über den Solarrechner ist kostenlos und unverbindlich. Du erhältst zuerst eine Einschätzung, ob eine PV-Anlage bei Deinem Haus technisch und wirtschaftlich sinnvoll ist. Erst nach Beratung und transparentem Angebot entscheidest Du, ob Du weitermachen möchtest." },
              { q: "Was kostet eine PV-Anlage insgesamt, inklusive Montage und allem?", a: "Eine schlüsselfertige 10-kWp-Anlage liegt brutto = netto (dank 0 % MwSt.) bei rund 16.000–22.000 €, inklusive Modulen, Montage, Wechselrichter, Anmeldung und Inbetriebnahme. Mit 8–10-kWh-Speicher rechnest du 22.000–30.000 €. Wir erstellen Festpreis-Angebote ohne Vorkasse und ohne versteckte Posten." },
              { q: "Wie lange dauert die Installation einer PV-Anlage wirklich?", a: "Reine Montage auf dem Dach: 1–2 Werktage. Elektroanschluss und Inbetriebnahme: 1–2 weitere Tage. Vom Auftrag bis zur Inbetriebnahme rechnen wir aktuell mit 6–12 Wochen – die meiste Zeit geht für Materialbeschaffung und Netzbetreiber-Anmeldung drauf, nicht für die Montage selbst." },
              { q: "Was passiert, wenn ich später aus dem Haus auszuziehe?", a: "PV-Anlagen erhöhen den Immobilienwert messbar (oft mit 80–100 % der Investitionssumme). Beim Hausverkauf wird die Anlage Teil der Immobilie und kann ein klares Verkaufsargument sein – der Käufer übernimmt sie inkl. Einspeisevergütung. Eine Mitnahme lohnt sich selten (Demontage + Wiederaufbau-Kosten)." },
              { q: "Was passiert, wenn ein Modul kaputt geht?", a: "Modul-Garantie beträgt 25–30 Jahre Hersteller-Standard und deckt Produktdefekte ab. Wir tauschen das Modul und wickeln die Garantie mit dem Hersteller ab. Bei externen Schäden (Hagel, Sturm) greift deine PV-Versicherung. Moderne Anlagen haben Optimierer pro Modul – ein Defekt beeinträchtigt nur dieses Modul, nicht die ganze Anlage." },
              { q: "Habe ich bei einem Stromausfall trotzdem Strom?", a: "Standard-PV-Anlagen schalten bei Netzausfall ab (Vorschrift zum Schutz der Netzmonteure). Mit Notstrom-Option (bei Sigenergy „Notstrom-Ready\" für +1.000 €) läuft die Anlage im Inselbetrieb weiter und versorgt deinen Haushalt. Für unterbrechungsfreie Versorgung sensibler Geräte gibt es Speicher mit Umschaltzeit unter 20 ms." },
              { q: "Wie laut ist der Wechselrichter im Betrieb?", a: "Moderne Wechselrichter (Sigenergy, Enphase) erzeugen im Volllastbetrieb ein dezentes Summen wie ein leiser PC-Lüfter (~30–40 dB in 1 m). Im Keller installiert nicht hörbar. Enphase-Mikrowechselrichter sind komplett geräuschlos, weil sie auf dem Dach an den Modulen sitzen." },
              { q: "Wie wartungsintensiv ist eine PV-Anlage?", a: "Sehr wartungsarm. Module reinigen sich durch Regen selbst, Verkabelung ist wartungsfrei. Der Wechselrichter ist die einzige aktive Komponente und hält typischerweise 10–15 Jahre. Monitoring ist bei uns inklusive – bei Fehlern wirst du automatisch kontaktiert. Klassische Wartungsverträge sind in der Regel nicht notwendig." },
              { q: "Was, wenn das Dach im Schatten liegt oder bewölkt ist?", a: "Moderne Module produzieren auch bei Bewölkung 10–30 % ihrer Nennleistung durch diffuses Licht. Verschattung durch Bäume oder Schornsteine fangen wir mit Modul-Optimierern (Tigo) oder Mikrowechselrichtern (Enphase) ab – jedes Modul läuft in seinem Optimum. Bei Schnee rutscht dieser meist in 1–3 Tagen ab." },
              { q: "Welche Informationen braucht WMEE für die Prüfung?", a: "Hilfreich sind Adresse, ungefährer Jahresstromverbrauch, Angaben zum Dach sowie Informationen zu Speicher, Wallbox, Wärmepumpe oder E-Auto. Falls vorhanden, helfen Bilder vom Dach, Zählerschrank und Hausanschlussraum." },
              { q: "Brauche ich Speicher, Wallbox oder Notstrom direkt von Anfang an?", a: "Nicht zwingend. PV kann auch ohne Speicher sinnvoll sein. Speicher, Wallbox oder Notstromlösung hängen von Deinem Verbrauch, E-Auto, Wärmepumpe und Sicherheitswunsch ab. Wichtig ist, die Anlage so zu planen, dass spätere Erweiterungen möglich bleiben." },
            ].map((it, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border rounded-xl px-5">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">{it.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{it.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
            <Link to="/faq" className="btn-primary w-full sm:w-auto">
              Alle FAQs ansehen <ArrowRight size={18} />
            </Link>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("open-chat"))}
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary px-6 sm:px-7 py-3 min-h-[48px] text-primary font-semibold uppercase tracking-wide text-sm transition-all hover:bg-primary hover:text-primary-foreground w-full sm:w-auto"
            >
              <MessageCircle size={18} /> Chat starten
            </button>
          </div>
        </div>
      </section>

      {/* CTA + Calendly nebeneinander auf Desktop */}
      <section className="section">
        <div className="container-wm">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 items-stretch">
            <div className="relative rounded-2xl md:rounded-3xl overflow-hidden p-6 sm:p-10 md:p-14 text-center flex flex-col justify-center min-h-[380px] md:min-h-[500px]">
              <img src="/images/Bild-1-Walldorf-800w.webp" srcSet="/images/Bild-1-Walldorf-480w.webp 480w, /images/Bild-1-Walldorf-800w.webp 800w, /images/Bild-1-Walldorf-1280w.webp 1280w" sizes="(min-width: 1024px) 50vw, 100vw" alt="" loading="lazy" decoding="async" width={1600} height={900} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/80 to-background/70" />
              <div className="relative">
                <h2 className="text-2xl sm:text-3xl md:text-4xl mb-4 md:mb-5 leading-tight">Bereit für deine eigene Energiezentrale?</h2>
                <p className="text-base sm:text-lg text-muted-foreground mb-6 md:mb-8 max-w-xl mx-auto leading-relaxed">
                  Berechne in wenigen Minuten deine persönliche PV-Anlage – kostenlos und unverbindlich.
                </p>
                <SolarrechnerButton className="w-full sm:w-auto justify-center" />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="mb-4 text-center lg:text-left">
                <p className="eyebrow mb-2 text-xs sm:text-sm">Termin vereinbaren</p>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
                  Direkt einen Termin auswählen
                </h3>
              </div>
              <Suspense fallback={<div className="min-h-[500px]" />}><CalendlyWidget embedded /></Suspense>
            </div>
          </div>
        </div>
      </section>

    </Layout>
  );
};

export default Index;
